import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import dat from "dat.gui";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

function CodeStructuringForBiggerProjects() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const currentDom = ref.current;
    if (!currentDom) return;

    THREE.ColorManagement.enabled = false;

    /**
     * Loaders
     */
    const gltfLoader = new GLTFLoader();
    const textureLoader = new THREE.TextureLoader();
    const cubeTextureLoader = new THREE.CubeTextureLoader();

    /**
     * Debug
     */
    const gui = new dat.GUI();
    const debugObject = {
      envMapIntensity: 0.4,
    };

    // Sizes
    const sizes = {
      width: currentDom.offsetWidth || 800,
      height: currentDom.offsetHeight || 600,
    };

    // Scene
    const scene = new THREE.Scene();

    /**
     * Update all materials
     */
    const updateAllMaterials = () => {
      scene.traverse((child) => {
        if (
          child instanceof THREE.Mesh &&
          child.material instanceof THREE.MeshStandardMaterial
        ) {
          // child.material.envMap = environmentMap
          child.material.envMapIntensity = debugObject.envMapIntensity;
          child.material.needsUpdate = true;
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
    };

    /**
     * Environment map
     */
    const environmentMap = cubeTextureLoader.load([
      `${process.env.PUBLIC_URL}/textures/environmentMaps/0/px.jpg`,
      `${process.env.PUBLIC_URL}/textures/environmentMaps/0/nx.jpg`,
      `${process.env.PUBLIC_URL}/textures/environmentMaps/0/py.jpg`,
      `${process.env.PUBLIC_URL}/textures/environmentMaps/0/ny.jpg`,
      `${process.env.PUBLIC_URL}/textures/environmentMaps/0/pz.jpg`,
      `${process.env.PUBLIC_URL}/textures/environmentMaps/0/nz.jpg`,
    ]);

    environmentMap.colorSpace = THREE.SRGBColorSpace;

    // scene.background = environmentMap
    scene.environment = environmentMap;

    debugObject.envMapIntensity = 0.4;
    gui
      .add(debugObject, "envMapIntensity")
      .min(0)
      .max(4)
      .step(0.001)
      .onChange(updateAllMaterials);

    /**
     * Models
     */
    let foxMixer: any = null;

    gltfLoader.load("/models/Fox/glTF/Fox.gltf", (gltf) => {
      // Model
      gltf.scene.scale.set(0.02, 0.02, 0.02);
      scene.add(gltf.scene);

      // Animation
      foxMixer = new THREE.AnimationMixer(gltf.scene);
      const foxAction = foxMixer.clipAction(gltf.animations[0]);
      foxAction.play();

      // Update materials
      updateAllMaterials();
    });

    /**
     * Floor
     */
    const floorColorTexture = textureLoader.load("/textures/dirt/color.jpg");
    floorColorTexture.colorSpace = THREE.SRGBColorSpace;
    floorColorTexture.repeat.set(1.5, 1.5);
    floorColorTexture.wrapS = THREE.RepeatWrapping;
    floorColorTexture.wrapT = THREE.RepeatWrapping;

    const floorNormalTexture = textureLoader.load("/textures/dirt/normal.jpg");
    floorNormalTexture.repeat.set(1.5, 1.5);
    floorNormalTexture.wrapS = THREE.RepeatWrapping;
    floorNormalTexture.wrapT = THREE.RepeatWrapping;

    const floorGeometry = new THREE.CircleGeometry(5, 64);
    const floorMaterial = new THREE.MeshStandardMaterial({
      map: floorColorTexture,
      normalMap: floorNormalTexture,
    });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI * 0.5;
    scene.add(floor);

    /**
     * Lights
     */
    const directionalLight = new THREE.DirectionalLight("#ffffff", 4);
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.far = 15;
    directionalLight.shadow.mapSize.set(1024, 1024);
    directionalLight.shadow.normalBias = 0.05;
    directionalLight.position.set(3.5, 2, -1.25);
    scene.add(directionalLight);

    gui
      .add(directionalLight, "intensity")
      .min(0)
      .max(10)
      .step(0.001)
      .name("lightIntensity");
    gui
      .add(directionalLight.position, "x")
      .min(-5)
      .max(5)
      .step(0.001)
      .name("lightX");
    gui
      .add(directionalLight.position, "y")
      .min(-5)
      .max(5)
      .step(0.001)
      .name("lightY");
    gui
      .add(directionalLight.position, "z")
      .min(-5)
      .max(5)
      .step(0.001)
      .name("lightZ");
    /**
     * Camera
     */
    // Base camera
    const camera = new THREE.PerspectiveCamera(
      35,
      sizes.width / sizes.height,
      0.1,
      100
    );
    camera.position.set(6, 4, 8);
    scene.add(camera);

    // Controls
    const controls = new OrbitControls(camera, currentDom);
    controls.enableDamping = true;

    /**
     * Renderer
     */
    const renderer = new THREE.WebGLRenderer({
      canvas: currentDom,
      antialias: true,
    });
    renderer.useLegacyLights = false;
    renderer.toneMapping = THREE.CineonToneMapping;
    renderer.toneMappingExposure = 1.75;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setClearColor("#211d20");
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    /**
     * Animate
     */
    const clock = new THREE.Clock();
    let previousTime = 0;

    const tick = () => {
      const elapsedTime = clock.getElapsedTime();
      const deltaTime = elapsedTime - previousTime;
      previousTime = elapsedTime;

      // Update controls
      controls.update();

      // Fox animation
      if (foxMixer) {
        foxMixer.update(deltaTime);
      }

      // Render
      renderer.render(scene, camera);

      // Call tick again on the next frame
      requestAnimationFrame(tick);
    };
    tick();

    return () => {
      gui.destroy();
    };
  }, []);

  return <canvas style={{ width: "100%", height: "100%" }} ref={ref} />;
}

export default CodeStructuringForBiggerProjects;
